/**
 * Promo Campaign — landing-page hook + components.
 *
 * usePromoCampaign()  Fetches /api/promo/campaign-status, ticks every second,
 *                     and auto-flips `active=false` at the end-of-window
 *                     boundary without requiring a page refresh.
 *
 * <PromoBanner />     Top-of-page banner with headline, subtext and a live
 *                     countdown. Renders nothing when the campaign is inactive
 *                     OR after manual dismissal (sessionStorage flag).
 *
 * <PromoBadge />      Small "⭐ Limited-Time Offer" pill rendered directly
 *                     above the primary signup button.
 *
 * <PromoTrust />      Sub-text rendered below the primary signup button.
 */
import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Gift } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Default copy — used as an instant fallback so the page renders without
// flicker while the GET completes. Server is the source of truth.
const FALLBACK = {
  campaign: 'premium_launch_june_2026',
  active: false,
  signupWindowStart: '2026-05-31T18:30:00+00:00',
  signupWindowEnd: '2026-06-07T18:29:59+00:00',
  premiumExpiresAt: '2026-06-30T18:29:59+00:00',
  postCampaignAt: '2026-06-30T18:30:00+00:00',
  copy: {
    bannerHeadline: '🎉 Free Premium Access Until 30 June 2026',
    bannerSubtext:
      'Sign up between 1 June 2026 and 7 June 2026 to unlock all premium features and updates at no cost until 30 June 2026.',
    countdownHeading: 'Offer Ends In:',
    signupBadge: '⭐ Limited-Time Offer: Free Premium Access Until 30 June 2026',
    signupButton: 'Claim Free Premium Access',
    signupTrust: 'No credit card required. Instant access. Offer valid through 7 June 2026.',
  },
};

/**
 * useCampaignCountdown — client-side ticker that re-computes seconds-left
 * on every animation frame interval. Auto-deactivates the moment the wall
 * clock crosses `endsAt` so the user doesn't need to refresh.
 */
function useCampaignCountdown(endsAtIso) {
  const endsAt = useMemo(() => (endsAtIso ? new Date(endsAtIso).getTime() : 0), [endsAtIso]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  const diff = Math.max(0, Math.floor((endsAt - now) / 1000));
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

export function usePromoCampaign() {
  const [status, setStatus] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/promo/campaign-status`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setStatus(data);
      } catch (e) {
        // Silently fall back — banner just stays hidden.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const countdown = useCampaignCountdown(status.signupWindowEnd);
  // `active` is true ONLY when the server says so AND the countdown hasn't
  // hit zero on the client. The double-check makes the auto-disappear work
  // even if the user keeps the page open across the boundary.
  const active = Boolean(status.active && !countdown.expired);
  return { status, countdown, active, loaded };
}

// ─────────────────────────────────────────────────────────────
// Banner — rendered above the header during the promo window.
// ─────────────────────────────────────────────────────────────
export function PromoBanner({ onCtaClick }) {
  const { status, countdown, active } = usePromoCampaign();
  if (!active) return null;
  const { copy } = status;
  return (
    <div
      data-testid="promo-banner"
      className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow-lg shadow-rose-500/20"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-base md:text-lg font-bold tracking-tight leading-snug" data-testid="promo-banner-headline">
            {copy.bannerHeadline}
          </div>
          <div className="hidden sm:block text-[11px] sm:text-xs text-white/90 mt-0.5" data-testid="promo-banner-subtext">
            {copy.bannerSubtext}
          </div>
        </div>
        <div className="flex items-end gap-3 sm:gap-4" data-testid="promo-countdown">
          <div className="text-right">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-white/80" data-testid="countdown-heading">
              {copy.countdownHeading}
            </div>
            <div className="flex items-end gap-1 sm:gap-2 font-mono tabular-nums mt-1">
              <CountdownCell value={countdown.days} label="D" />
              <span className="text-base sm:text-xl font-bold pb-0.5">:</span>
              <CountdownCell value={countdown.hours} label="H" />
              <span className="text-base sm:text-xl font-bold pb-0.5">:</span>
              <CountdownCell value={countdown.minutes} label="M" />
              <span className="text-base sm:text-xl font-bold pb-0.5">:</span>
              <CountdownCell value={countdown.seconds} label="S" pulse />
            </div>
          </div>
          <button
            type="button"
            onClick={onCtaClick}
            className="hidden md:inline-flex items-center gap-1.5 rounded-lg bg-white text-rose-700 hover:bg-amber-50 transition-colors px-4 py-2 text-sm font-semibold shadow-md"
            data-testid="promo-banner-cta"
          >
            <Gift className="h-4 w-4" /> {copy.signupButton}
          </button>
        </div>
      </div>
    </div>
  );
}

function CountdownCell({ value, label, pulse = false }) {
  const padded = String(value).padStart(2, '0');
  return (
    <div className="flex flex-col items-center">
      <span
        className={`text-base sm:text-2xl font-extrabold leading-none ${pulse ? 'animate-pulse' : ''}`}
        data-testid={`countdown-${label.toLowerCase()}`}
      >
        {padded}
      </span>
      <span className="text-[8px] sm:text-[9px] uppercase tracking-widest text-white/80 mt-0.5">{label}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Badge — rendered immediately ABOVE the primary signup button.
// ─────────────────────────────────────────────────────────────
export function PromoBadge() {
  const { status, active } = usePromoCampaign();
  if (!active) return null;
  return (
    <div
      data-testid="promo-signup-badge"
      className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 border border-amber-300 px-3 py-1.5 text-xs sm:text-sm font-semibold text-rose-800 shadow-sm mb-2 sm:mb-3"
    >
      <Sparkles className="h-3.5 w-3.5 text-amber-600" />
      <span>{status.copy.signupBadge}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Trust message — rendered immediately BELOW the primary signup button.
// ─────────────────────────────────────────────────────────────
export function PromoTrust() {
  const { status, active } = usePromoCampaign();
  if (!active) return null;
  return (
    <p
      data-testid="promo-signup-trust"
      className="mt-2 text-[11px] sm:text-xs text-slate-600 max-w-md"
    >
      {status.copy.signupTrust}
    </p>
  );
}

// Re-exports for convenience.
export default PromoBanner;
